import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-responsive-modal';
import { Formik, Form, Field } from 'formik';
import { Button } from '@synergy-marine-group/synergy-ui';
import DropdownFieldComponent from '../common/FormComponents/DropdownFieldComponent';
import InputFieldComponent from '../common/FormComponents/InputFieldComponent';
import { deleteModalStyles } from './styles';
import getShortCloseReasons from '../po/queries/getShortCloseReasons';
import showAlert from '../../utils/alert';
import Loader from '../common/Loading';
import StatusIndicator from '../common/StatusIndicator';
import {
  initializeApproverLevel,
  getInitialValues,
  preparePayload,
  updateApproverEmail
} from '../../utils/shortCloseHelpers';
import { getValidationSchema } from '../../utils/shortClosevalidationSchema';
import updateVendorEmail from '../po/queries/updateVendorEmail';

const RequestShortClose = ({ modal, isLoading, setModal, handleDelete,
  approvalConfig, vendorId, purchaseOrderId, lineItemId }) => {
  const [emailChanged, setEmailChanged] = useState(false);
  const [emailUpdateNotification, setEmailUpdateNotification] = useState(false);
  const [updatedEmail, setUpdatedEmail] = useState('');
  const [approverLevels, setApproverLevels] = useState({});
  const [reasonOptions, setReasonOptions] = useState([]);
  const { data: shortCloseReasons } = getShortCloseReasons();

  const { mutate: initiateEmailUpdate, isLoading: isUpdating } = updateVendorEmail();

  useEffect(() => {
    if (shortCloseReasons?.data?.length) {
      const options = shortCloseReasons.data.map(
        ({ reasonName: label, reasonId: value }) => ({ label, value })
      );
      setReasonOptions(options);
    }
  }, [shortCloseReasons]);

  useEffect(() => {
    if (!approvalConfig) return;

    const { shortCloseInitiatedDetails, approvers } = approvalConfig;
    const isUpdated = shortCloseInitiatedDetails?.isVendorEmailUpdated;
    setEmailChanged(isUpdated || false);

    const levels = {};
    [1, 2].forEach((level) => {
      const approver = approvers.find((a) => a.level === level);
      const initialized = initializeApproverLevel(approver, shortCloseInitiatedDetails, level);
      levels[level] = initialized;

      if (approver?.approverType === 1) setUpdatedEmail(approver.emailOfVendor);
    });
    setApproverLevels(levels);
  }, [approvalConfig]);

  const cancelFunction = () => setModal(false);

  const handleEmailUpdate = () => {
    initiateEmailUpdate(
      { vendorId, purchaseOrderId, lineItemId },
      {
        onSuccess: () => {
          const updatedLevels = updateApproverEmail(approverLevels, updatedEmail);
          setApproverLevels(updatedLevels);
          setEmailUpdateNotification(true);
          setEmailChanged(false);
          showAlert('Vendor email updated successfully.', 'success');
        }
      }
    );
  };

  const validationSchema = useMemo(
    () => getValidationSchema(reasonOptions, approvalConfig),
    [reasonOptions, approvalConfig]
  );
    const initialValues = useMemo(() => getInitialValues(
    approvalConfig,
    reasonOptions,
    approverLevels?.[1]?.email,
    approverLevels?.[2]?.email,
    approverLevels?.[1]?.dynamicLabel,
    approverLevels?.[2]?.dynamicLabel
  ), [approvalConfig, reasonOptions, approverLevels]);

  const onSubmit = (values) => {
    const payload = preparePayload(
      values,
      approverLevels?.[1]?.type,
      approverLevels?.[1]?.users,
      approverLevels?.[2]?.type,
      approverLevels?.[2]?.users,
      reasonOptions,
      approvalConfig
    );
    handleDelete(payload);
  };

  const renderAssigneeField = (level) => {
    const {
      type,
      dynamicLabel,
      options,
      status,
      approverType
    } = approverLevels?.[level] || {};

    const name = `level${level}Assignee`;
    const isVendor = type === 'vendor';
    const disabled = approvalConfig?.shortCloseInitiatedDetails?.isShortCloseInitiated;

    return (
      <div className="relative w-full">
        <Field
          name={name}
          component={isVendor ? InputFieldComponent : DropdownFieldComponent}
          className="w-full"
          size="large"
          rounded
          id={name}
          type={isVendor && !disabled ? 'email' : 'text'}
          options={options}
          label={`Level ${level} - ${dynamicLabel}`}
          maxMenuHeight={150}
          requiredField
          disabled={isVendor || disabled}
        />
        {emailChanged && approverType === 1 && (
          <div className="absolute top-0 right-0 mt-[2px] pr-1 flex items-center gap-1 text-xs font-mulish">
            <span className="text-black">Vendor email changed.</span>
            <button type="button" onClick={handleEmailUpdate} className="update-btn hover:underline focus:outline-none">
              Update
            </button>
          </div>
        )}
        {status && (
          <StatusIndicator
            status={status}
            position={isVendor ? 'right-3' : 'right-10'}
          />
        )}
      </div>
    );
  };

  return (
    <Modal
      open={modal}
      onClose={cancelFunction}
      closeOnOverlayClick={false}
      center
      showCloseIcon={false}
      styles={{ ...deleteModalStyles, modal: { ...deleteModalStyles.modal, width: '600px' } }}
    >
      <div className="w-auto py-8 px-8 font-muli relative">
        <h2 className={`uppercase text-center font-montserrat mb-4 font-bold p-1 ${approvalConfig?.shortCloseInitiatedDetails?.isShortCloseInitiated ? 'short-close-header' : ''}`}>
          {approvalConfig?.shortCloseInitiatedDetails?.isShortCloseInitiated
            ? 'Request Initiated for Short Close'
            : 'Request for Short Close'}
        </h2>

        {emailUpdateNotification && (
          <div className="modal-center-wrapper">
            <p className="email-notification">
              Email has been sent to
              <span className="pl-1">{updatedEmail}</span>
            </p>
          </div>
        )}

        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ values }) => (
            <Form className="space-y-6 mt-4">
              <Field
                name="reason"
                component={DropdownFieldComponent}
                className="w-full"
                options={reasonOptions}
                size="large"
                rounded
                id="reason"
                label="Reason for Short Close"
                maxMenuHeight={100}
                requiredField
                isDisabled={approvalConfig?.shortCloseInitiatedDetails?.isShortCloseInitiated}
              />
              {values.reason && reasonOptions.find((r) => r.value === values.reason)?.label === 'Other' && (
                <Field
                  name="otherReason"
                  component={InputFieldComponent}
                  className="w-full"
                  size="large"
                  rounded
                  id="otherReason"
                  type="text"
                  maxLength={250}
                  disabled={approvalConfig?.shortCloseInitiatedDetails?.isShortCloseInitiated}
                />
              )}
              {renderAssigneeField(1)}
              {((approvalConfig.numberOfLevels === 2
                && approvalConfig?.shortCloseInitiatedDetails?.isShortCloseInitiated === false)
                || approvalConfig?.shortCloseInitiatedDetails?.shortCloseLevelDetails?.level2
              ) && renderAssigneeField(2)}
              <Field
                name="remarks"
                component={InputFieldComponent}
                className="w-full pt-3 text-base mb-4"
                id="remarks"
                type="text"
                rounded
                autoComplete="off"
                label="Remarks"
                multiline
                placeholder="Type Remarks"
                disabled={approvalConfig?.shortCloseInitiatedDetails?.isShortCloseInitiated}
              />
              <div className="flex justify-between pt-4">
                <Button
                  className="font-montserrat text-gray-500 uppercase text-sm"
                  onClick={cancelFunction}
                  size="large"
                  variant="outlined"
                  rounded
                >
                  Cancel
                </Button>
                {!approvalConfig?.shortCloseInitiatedDetails?.isShortCloseInitiated && (
                  <Button
                    className="font-montserrat text-gray-500 uppercase text-sm"
                    type="submit"
                    size="large"
                    variant="filled"
                    rounded
                  >
                    Request Short Close
                  </Button>
                )}
              </div>
            </Form>
          )}
        </Formik>
        {(isLoading || isUpdating) && (
          <div className="absolute inset-0 bg-white bg-opacity-60 z-50 flex items-center justify-center">
            <Loader message="Loading" />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default RequestShortClose;
